/**
 * The referral program's closed lists and published numbers, both halves (docs/PLAN-referral-program-20261019.md).
 *
 * functions/src/referrals/referralVocabulary.ts is the server's copy and src/constants/referralVocabulary.ts the
 * browser's. `functions/` and `src/` cannot import each other, so the two are byte-identical and every list is equal at
 * run time (tests/referrals/referralVocabulary.parity.test.ts). Each side takes its words from here, never a union of its
 * own: a word one side writes that the other cannot read is how they silently drift apart. The program's numbers live
 * here for the same reason: the pages promise exactly what the server pays.
 */

/**
 * The terms a referrer accepts: their effective date (src/config/legalVersions.ts `referral`), with `.N` for a later
 * version published the same day. A new version asks everyone to accept again.
 */
export const REFERRAL_TERMS_VERSION = '2026-10-20';

/** What each milestone pays a referrer, in cents. */
export const REFERRAL_REWARD_CENTS = { month1: 50_000, month3: 50_000, annual: 100_000 } as const;

/** The most one referred organization can ever earn its referrer, in cents. */
export const REFERRAL_REWARD_CAP_CENTS = 100_000;

/** What the person who signs up through a link starts with: $500 of work units for 14 days. */
export const REFERRED_OFFER = { creditAmountCents: 50_000, durationDays: 14 } as const;

/** A link click stays attributable this long on the device that clicked it. */
export const REFERRAL_ATTRIBUTION_DAYS = 30;

/**
 * A reward is credited this many days after the payment that earns it, unless that payment is refunded or disputed
 * first (a refund takes its share, a dispute all of it, before any money moves). A stolen card's chargeback usually
 * comes within this window, so a reward cannot leave as cash before the payment behind it has held.
 */
export const REFERRAL_CLEARING_DAYS = 30;

/** A new account may take a link's offer for this many days after it was made (the claim normally runs at once). */
export const REFERRAL_NEW_ACCOUNT_DAYS = 3;

/** Where the program lives on the web. */
export const REFERRAL_SITE_ORIGIN = 'https://prest0.ai';

/** A code's link and short link. */
export function referralLinks(code: string): { link: string; shortLink: string } {
  return {
    link: `${REFERRAL_SITE_ORIGIN}/referrals/join/${code}`,
    shortLink: `${REFERRAL_SITE_ORIGIN}/r/${code}`,
  };
}

/**
 * A code as typed, pasted or read off a link: case and separators do not matter. Null when it cannot be a code, so a
 * lookup never runs on junk.
 */
export function normalizeReferralCode(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const code = raw.trim().toUpperCase().replace(/[\s-]/g, '');
  return /^[A-Z0-9]{6,12}$/.test(code) ? code : null;
}

/** Where a referred organization stands in the program. */
export const REFERRAL_STATUSES = [
  // The account was made through the link; its $500 start is being set up.
  'signed-up',
  // The referred organization is in its 14-day start.
  'trial',
  // The referred organization has paid at least one invoice.
  'hired',
  // Every reward this organization can earn has been paid ($1,000).
  'completed',
  // No longer earns rewards (a payment refunded in full, a chargeback, or a review found it was not a real new
  // organization). A reward another payment earned that is still clearing or held is paid unless a review ended it.
  'void',
] as const;
export type ReferralStatus = (typeof REFERRAL_STATUSES)[number];

/** The three moments a referrer is paid for one organization. */
export const REFERRAL_MILESTONES = ['month1', 'month3', 'annual'] as const;
export type ReferralMilestone = (typeof REFERRAL_MILESTONES)[number];

/** What became of a milestone's reward. */
export const REFERRAL_MILESTONE_STATES = [
  // Credited to the referrer's wallet, withdrawable.
  'paid',
  // Taken back, whole or in part, because the payment behind it was refunded or disputed.
  'reversed',
  // Earned but not yet credited: held while the referrer's participation is suspended, payouts are paused or the
  // day's price cannot be read, and credited (less any share refunded meanwhile) once that ends and it has cleared.
  'withheld',
  // Earned and on its way: credited automatically REFERRAL_CLEARING_DAYS after the payment that earned it (less any
  // share refunded meanwhile; a dispute takes it all).
  'clearing',
] as const;
export type ReferralMilestoneState = (typeof REFERRAL_MILESTONE_STATES)[number];

/** A referrer's standing in the program. */
export const REFERRER_STATUSES = ['active', 'suspended'] as const;
export type ReferrerStatus = (typeof REFERRER_STATUSES)[number];

/** Why a new account could not take a referral offer (the guardrails, in the order they are checked). */
export const REFERRAL_CLAIM_REFUSALS = [
  'program-paused',
  'code-unknown',
  'referrer-inactive',
  'self-referral',
  'already-referred',
  'not-a-new-account',
  'already-started',
  'joined-an-organization',
  'email-already-referred',
  'device-already-referred',
  'device-unknown',
  // More new accounts took referral offers from this network today than a real office makes.
  'network-limit',
] as const;
export type ReferralClaimRefusal = (typeof REFERRAL_CLAIM_REFUSALS)[number];

/** Signals a review should look at; none of them stops a referral by itself. */
export const REFERRAL_FLAGS = [
  // The new account signed up from the same network as the referrer.
  'same-network-as-referrer',
  // A payment behind a reward was refunded.
  'refunded',
  // A payment behind a reward was disputed.
  'disputed',
  // A reversal could not take the whole reward back (it had already been withdrawn or spent).
  'reversal-shortfall',
] as const;
export type ReferralFlag = (typeof REFERRAL_FLAGS)[number];

/** What the page may ask the referralProgram callable to do. */
export const REFERRAL_PROGRAM_ACTIONS = [
  'status',
  'enroll',
  'claim',
  'org-overview',
  'admin-overview',
  'admin-void-referral',
  'admin-suspend-referrer',
  'admin-reinstate-referrer',
] as const;
export type ReferralProgramAction = (typeof REFERRAL_PROGRAM_ACTIONS)[number];
